/**
 * Chat API Service
 * API calls for AI chatbot interactions
 */

import { api } from './api';
import type {
  SendMessageRequest,
  SendMessageResponse,
  ListSessionsRequest,
  ListSessionsResponse,
  GetSessionResponse,
  ClearSessionsRequest,
  ClearSessionsResponse,
  ChatStatsResponse,
} from '../types/help.types';

// Base URL for chat endpoints
const CHAT_URL = '/help/chat';

/**
 * Send a message to the AI chatbot and get a response
 */
export const sendMessage = async (
  request: SendMessageRequest
): Promise<SendMessageResponse> => {
  const response = await api.post<{ data: SendMessageResponse }>(
    CHAT_URL,
    request
  );
  return response.data.data;
};

/**
 * List user's chat sessions
 */
export const listSessions = async (
  params?: ListSessionsRequest
): Promise<ListSessionsResponse> => {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      searchParams.append('offset', params.offset.toString());
    }
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `${CHAT_URL}/sessions?${queryString}`
    : `${CHAT_URL}/sessions`;

  const response = await api.get<{ data: ListSessionsResponse }>(url);
  return response.data.data;
};

/**
 * Get a specific chat session with full message history
 */
export const getSession = async (sessionId: string): Promise<GetSessionResponse> => {
  const response = await api.get<{ data: GetSessionResponse }>(
    `${CHAT_URL}/sessions/${sessionId}`
  );
  return response.data.data;
};

/**
 * Delete a specific chat session
 */
export const deleteSession = async (
  sessionId: string
): Promise<{ message: string; sessionId: string }> => {
  const response = await api.delete<{
    data: { message: string; sessionId: string };
  }>(`${CHAT_URL}/sessions/${sessionId}`);
  return response.data.data;
};

/**
 * Clear all chat sessions (optionally keep active ones)
 */
export const clearSessions = async (
  request?: ClearSessionsRequest
): Promise<ClearSessionsResponse> => {
  const response = await api.post<{ data: ClearSessionsResponse }>(
    `${CHAT_URL}/sessions/clear`,
    request || {}
  );
  return response.data.data;
};

/**
 * Archive a chat session (mark as inactive)
 */
export const archiveSession = async (
  sessionId: string
): Promise<{ message: string; sessionId: string }> => {
  const response = await api.patch<{
    data: { message: string; sessionId: string };
  }>(`${CHAT_URL}/sessions/${sessionId}/archive`);
  return response.data.data;
};

/**
 * Get chat statistics (usage, limits, etc.)
 */
export const getChatStats = async (): Promise<ChatStatsResponse> => {
  const response = await api.get<{ data: ChatStatsResponse }>(
    `${CHAT_URL}/stats`
  );
  return response.data.data;
};

// Export all functions as a service object
export const chatApi = {
  sendMessage,
  listSessions,
  getSession,
  deleteSession,
  clearSessions,
  archiveSession,
  getChatStats,
};

export default chatApi;
